import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  askDbAnalyst,
  deleteDbSavedQuery,
  listDbSavedQueries,
  saveDbQuery,
  type DbChatResult,
  type SavedDbQuery,
} from "../../api/ai";
import { ApiClientError } from "../../api/types";
import { useAuth } from "../../context/AuthContext";
import { RoleCode } from "../../lib/rbac";
import { DynamicChartPanel } from "./DynamicChartPanel";
import { AiDbResultsGrid } from "./AiDbResultsGrid";

export function AiDbCommandBar() {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const allowed = hasRole(RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR);

  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DbChatResult | null>(null);
  const [saved, setSaved] = useState<SavedDbQuery[]>([]);
  const [saveLabel, setSaveLabel] = useState("");
  const [showSave, setShowSave] = useState(false);

  useEffect(() => {
    if (!open || !allowed) return;
    void listDbSavedQueries()
      .then(setSaved)
      .catch(() => setSaved([]));
  }, [open, allowed]);

  const run = useCallback(
    async (q: string) => {
      const text = q.trim();
      if (!text || loading) return;
      setLoading(true);
      setError(null);
      try {
        setResult(await askDbAnalyst(text));
      } catch (err) {
        setResult(null);
        setError(err instanceof ApiClientError ? err.message : t("ai.send_failed"));
      } finally {
        setLoading(false);
      }
    },
    [loading, t],
  );

  useEffect(() => {
    if (!allowed) return;
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [allowed]);

  if (!allowed) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="hidden rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-medium text-violet-800 hover:bg-violet-100 md:inline-flex"
        title="Ctrl+K"
      >
        ✨ {t("ai.db_command_trigger", { defaultValue: "اسأل قاعدة البيانات" })}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-start justify-center bg-slate-900/50 p-4 pt-[12vh]"
          role="dialog"
          aria-modal
        >
          <div
            className="flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
            dir="rtl"
          >
            <div className="border-b border-slate-100 px-4 py-3">
              <p className="text-sm font-bold text-slate-800">
                {t("ai.db_command_title", { defaultValue: "محلل قاعدة البيانات — Text-to-SQL" })}
              </p>
              <p className="text-[11px] text-slate-500">
                {t("ai.db_command_subtitle", {
                  defaultValue: "السؤال + مخطط الجداول فقط يذهب للذكاء؛ التنفيذ على D1 داخل الخادم.",
                })}
              </p>
            </div>

            <div className="flex flex-wrap gap-1.5 border-b border-slate-50 px-4 py-2">
              {saved.map((s) => (
                <span key={s.queryId} className="inline-flex items-center gap-0.5">
                  <button
                    type="button"
                    className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-medium text-slate-700 hover:bg-violet-100"
                    onClick={() => {
                      setQuestion(s.question);
                      void run(s.question);
                    }}
                  >
                    {s.label}
                  </button>
                  {!s.builtin && (
                    <button
                      type="button"
                      className="rounded px-1 text-[10px] text-slate-400 hover:text-red-600"
                      title="حذف"
                      onClick={() => {
                        void deleteDbSavedQuery(s.queryId).then(() =>
                          setSaved((prev) => prev.filter((x) => x.queryId !== s.queryId)),
                        );
                      }}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>

            <div className="flex gap-2 border-b border-slate-100 px-4 py-3">
              <input
                autoFocus
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void run(question);
                }}
                placeholder={t("ai.db_chat_placeholder", {
                  defaultValue: "مثال: كشف تكرار أرقام الفواتير",
                })}
                className="flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-200"
              />
              <button
                type="button"
                disabled={loading || !question.trim()}
                onClick={() => void run(question)}
                className="rounded-xl bg-violet-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-40"
              >
                {loading ? "…" : t("ai.db_chat_run", { defaultValue: "تنفيذ" })}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600"
              >
                Esc
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
              {error && (
                <p className="mb-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
              )}
              {result && (
                <div className="space-y-3">
                  <p className="text-sm leading-relaxed text-slate-800">{result.narrative}</p>
                  <p className="text-[10px] text-slate-400" dir="ltr">
                    {result.rowCount} rows
                    {result.executionMs != null ? ` · ${result.executionMs}ms` : ""}
                    {result.llmProvider
                      ? ` · ${result.llmProvider}${result.llmModel ? ` (${result.llmModel})` : ""}`
                      : ""}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="text-[11px] font-medium text-violet-700 hover:underline"
                      onClick={() => {
                        setSaveLabel(question.slice(0, 48) || "استعلام محفوظ");
                        setShowSave((v) => !v);
                      }}
                    >
                      {t("ai.db_save_query", { defaultValue: "حفظ هذا السؤال" })}
                    </button>
                    {showSave && (
                      <>
                        <input
                          value={saveLabel}
                          onChange={(e) => setSaveLabel(e.target.value)}
                          className="rounded border border-slate-200 px-2 py-0.5 text-[11px]"
                          placeholder="اسم مختصر"
                        />
                        <button
                          type="button"
                          className="rounded bg-slate-800 px-2 py-0.5 text-[11px] text-white"
                          onClick={() => {
                            const q = question.trim();
                            const lab = saveLabel.trim();
                            if (!lab || !q) return;
                            void saveDbQuery(lab, q).then((item) => {
                              setSaved((prev) => [...prev.filter((x) => x.queryId !== item.queryId), item]);
                              setShowSave(false);
                            });
                          }}
                        >
                          OK
                        </button>
                      </>
                    )}
                  </div>
                  <AiDbResultsGrid columns={result.columns} rows={result.rows} />
                  <DynamicChartPanel
                    columns={result.columns}
                    rows={result.rows}
                    chart={result.chart}
                  />
                  <details className="text-[10px] text-slate-500">
                    <summary className="cursor-pointer font-medium">SQL</summary>
                    <pre className="mt-1 overflow-x-auto font-mono" dir="ltr">
                      {result.sql}
                    </pre>
                  </details>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
